import React, { useState } from 'react';
import Footer from '../general/Footer';
import { HEIGHTS } from './constants';
import { Stack, IconButton, Tooltip, Alert } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import SaveIcon from '@mui/icons-material/Save';
import { StyledMenu, joinDateAndUser } from '../../common/utils/common.util';
import { Button, Typography, Box } from 'components/index';
import { ConditionalTooltipProps, TOOLTIPS } from './report.util';
import { ReportStatusEnum } from './report.type';
import { KeyboardArrowUp } from '@mui/icons-material';
import { PermissionGate } from '../common/PermissionGate';

interface ReportProps {
  report: any;
  isReportDirty: boolean;
  isLocked: boolean;
  isReportAuthorSameAsLoggedInUser: boolean;
  isDeletingReport: boolean;
  isAddingComps: boolean;
  isReportLoading: boolean;
  toggleLock: () => void;
  onDeleteReport: () => void;
  handleOpenCompsDialog: () => void;
  handleSavePdf: () => void;
  handleCreateCopy: () => void;
  handleGlobalAddComps: () => void;
  saveReport: () => void;
  isSaving: boolean;
  onExportInvoice?: () => void;
}

const ReportFooter: React.FC<ReportProps> = ({
  report,
  isReportDirty,
  isLocked,
  isReportAuthorSameAsLoggedInUser,
  isDeletingReport,
  isAddingComps,
  isReportLoading,
  toggleLock,
  onDeleteReport,
  handleOpenCompsDialog,
  handleSavePdf,
  handleCreateCopy,
  handleGlobalAddComps,
  saveReport,
  isSaving,
  onExportInvoice,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Footer height={HEIGHTS.FOOTER_HEIGHT}>
      <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} flex={1}>
        {/* Left side for locking and date info */}
        <Stack direction={'column'} spacing={0}>
          {!isReportAuthorSameAsLoggedInUser && (
            <Box
              onClick={toggleLock}
              style={{
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                paddingLeft: 0,
                marginLeft: 0,
              }}
              aria-label={isLocked ? 'Unlock Report' : 'Lock Report'}
            >
              <Tooltip
                title={
                  isLocked && report.status === ReportStatusEnum.Completed
                    ? TOOLTIPS.statusCompleted
                    : isLocked
                      ? 'Unlock Report'
                      : 'Lock Report'
                }
                arrow={false}
              >
                <span>
                  <IconButton
                    color='primary'
                    disabled={(isReportDirty && !isLocked) || report.status == 'Completed'}
                    size='medium'
                    component='span'
                    style={{
                      marginLeft: 0,
                      paddingLeft: 0,
                      marginRight: 0,
                      paddingRight: 0,
                    }}
                  >
                    {isLocked ? <LockIcon /> : <LockOpenIcon />}
                  </IconButton>
                </span>
              </Tooltip>
              <Typography
                variant='subtitle2'
                style={{
                  margin: 0,
                  padding: 0,
                }}
              >
                {isLocked ? 'Unlock Report' : 'Lock Report'}
              </Typography>
            </Box>
          )}
          <Typography variant='subtitle2'>{joinDateAndUser(report.createdOn, report.createdBy, 'Generated on ')}</Typography>
        </Stack>

        {/* Center for Alerts */}
        <Stack direction={'column'} spacing={1} flex={1} alignItems='center'>
          {isReportDirty ? <Alert severity='warning'>Report has unsaved changes</Alert> : null}
          {isLocked && report.status === ReportStatusEnum.Completed ? (
            <Alert severity='info'>Report is marked as completed and cannot be unlocked. Please make a copy to edit</Alert>
          ) : isLocked ? (
            <Alert severity='info'>Unlock the report to enable editing</Alert>
          ) : null}
        </Stack>

        {/* Right side for actions */}
        <Stack direction={'row'} spacing={1}>
          <Tooltip title={'More Actions'} arrow={false}>
            <Button startIcon={<KeyboardArrowUp />} variant='outlined' onClick={handleClick}>
              Actions
            </Button>
          </Tooltip>
          <Tooltip
            title={
              !isReportDirty || isLocked
                ? isLocked && report.status === ReportStatusEnum.Completed
                  ? TOOLTIPS.statusCompleted
                  : isLocked
                    ? TOOLTIPS.unlockToEdit
                    : ''
                : ''
            }
            disableHoverListener={!isReportDirty && !isLocked}
            arrow={false}
          >
            <span>
              <Button
                variant='contained'
                onClick={saveReport}
                loading={isSaving}
                disabled={!isReportDirty || isLocked}
                startIcon={<SaveIcon />}
              >
                Save
              </Button>
            </span>
          </Tooltip>
          <StyledMenu
            id='long-menu'
            MenuListProps={{
              'aria-labelledby': 'long-button',
            }}
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            slotProps={{
              paper: {
                sx: {
                  maxHeight: 48 * 5.5,
                  width: '240px',
                  background: 'transparent !important',
                  boxShadow: 'none !important',
                  overflow: 'visible',
                },
              },
            }}
            anchorOrigin={{
              vertical: 'top',
              horizontal: -20,
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
          >
            <PermissionGate actionRole='DELETE_CA_REPORT'>
              <ConditionalActionsTooltip isLocked={isLocked} reportStatus={report.status}>
                <MenuItem
                  onClick={() => {
                    if (!isLocked) {
                      onDeleteReport();
                      handleClose();
                    }
                  }}
                  disabled={isLocked}
                >
                  DELETE REPORT
                </MenuItem>
              </ConditionalActionsTooltip>
            </PermissionGate>
            <ConditionalActionsTooltip isLocked={isLocked} reportStatus={report.status}>
              <MenuItem
                onClick={() => {
                  if (!isLocked) {
                    handleGlobalAddComps();
                    handleClose();
                  }
                }}
                disabled={isLocked}
              >
                ADD COMPARABLE PRICING
              </MenuItem>
            </ConditionalActionsTooltip>
            <ConditionalActionsTooltip isLocked={isLocked} reportStatus={report.status}>
              <MenuItem
                onClick={() => {
                  if (!isLocked) {
                    handleOpenCompsDialog();
                    handleClose();
                  }
                }}
                disabled={isLocked}
              >
                ADD COMPARABLES
              </MenuItem>
            </ConditionalActionsTooltip>
            <MenuItem
              onClick={() => {
                handleSavePdf();
                handleClose();
              }}
            >
              EXPORT AS PDF
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleCreateCopy();
                handleClose();
              }}
            >
              DUPLICATE REPORT
            </MenuItem>
            {onExportInvoice && (
              <Tooltip title={'Export Invoice'} arrow={false}>
                <MenuItem onClick={onExportInvoice}>EXPORT INVOICE</MenuItem>
              </Tooltip>
            )}
          </StyledMenu>
        </Stack>
      </Stack>
    </Footer>
  );
};

export default React.memo(ReportFooter);

function ConditionalActionsTooltip(props: ConditionalTooltipProps) {
  const { children, isLocked, reportStatus } = props;
  const style = { marginBottom: '10px' };
  if (isLocked) {
    return (
      <Tooltip
        title={isLocked && reportStatus === ReportStatusEnum.Completed ? TOOLTIPS.statusCompleted : isLocked ? TOOLTIPS.unlockToEdit : ''}
        placement='left'
      >
        <div style={style}>{children}</div>
      </Tooltip>
    );
  }

  return <>{children}</>;
}
